import { Loader2Icon } from 'lucide-react'
import type { ReactNode } from 'react'

import { Command, CommandEmpty, CommandInput, CommandList } from '@/components/ui/command'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

import type { GlobalSearchProps } from './global-search.interface'
import { SearchResultGroup } from './global-search-result-group.molecule'
import { GlobalSearchTrigger } from './global-search-trigger.atom'
import { useGlobalSearch } from './use-global-search.hook'

const COMMAND_DIALOG_CLASS =
  '[&_[cmdk-group-heading]]:text-muted-foreground **:data-[slot=command-input-wrapper]:h-12 [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group]]:px-2 [&_[cmdk-group]:not([hidden])_~[cmdk-group]]:pt-0 [&_[cmdk-input-wrapper]_svg]:h-5 [&_[cmdk-input-wrapper]_svg]:w-5 [&_[cmdk-input]]:h-12 [&_[cmdk-item]]:px-2 [&_[cmdk-item]]:py-3 [&_[cmdk-item]_svg]:h-5 [&_[cmdk-item]_svg]:w-5'

/** Global search palette with Cmd+K trigger, supporting static and API-driven results */
export const GlobalSearch = ({
  placeholder = 'Type a command or search...',
  resultGroupList,
  onSearch,
  onResultSelect,
  isLoading,
}: GlobalSearchProps): ReactNode => {
  const { isOpen, openDialog, handleOpenChange, handleSelect } = useGlobalSearch({
    onSearch,
    onResultSelect,
  })

  const visibleGroupList = resultGroupList.filter((group) => group.itemList.length > 0)
  const hasVisibleResults = visibleGroupList.length > 0

  return (
    <>
      <GlobalSearchTrigger onClick={openDialog} />
      <Dialog open={isOpen} onOpenChange={handleOpenChange}>
        <DialogHeader className="sr-only">
          <DialogTitle>Global Search</DialogTitle>
          <DialogDescription>Search for projects, agents, chats, and more</DialogDescription>
        </DialogHeader>
        <DialogContent className="overflow-hidden p-0" showCloseButton={false}>
          <Command shouldFilter={false} className={COMMAND_DIALOG_CLASS}>
            <CommandInput
              placeholder={placeholder}
              onValueChange={onSearch}
              className="global-search-input"
            />
            <CommandList className="global-search-results">
              {!hasVisibleResults && !isLoading && <CommandEmpty>No results found.</CommandEmpty>}
              {visibleGroupList.map((group, index) => (
                <SearchResultGroup
                  key={group.type}
                  group={group}
                  onSelect={handleSelect}
                  isLast={index === visibleGroupList.length - 1 && !isLoading}
                />
              ))}
              {isLoading && (
                <div className="global-search-loading flex items-center justify-center py-4">
                  <Loader2Icon className="size-5 animate-spin text-muted-foreground" />
                </div>
              )}
            </CommandList>
          </Command>
        </DialogContent>
      </Dialog>
    </>
  )
}
